/**
 * Product Card Component for WoodAlps Bicycle Display
 * 
 * This component renders individual product cards in product grids and listings.
 * Features:
 * - Responsive image handling with hover effects
 * - Dynamic fallback images based on product category
 * - Product specifications display (material, weight, etc.)
 * - Pricing information with currency formatting
 * - Link navigation to detailed product pages
 * - Optimized images with Next.js Image component
 * 
 * Image Handling:
 * - Primary cover image with hover transition
 * - Category-based fallback images (MTB, Road, Gravel)
 * - Sanity CMS image optimization
 * - Responsive sizing for different screen sizes
 * 
 * @description Reusable product card for bike catalog display
 */

import Image from 'next/image';

import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import Link from 'next/link';

// Standard Product Card Component for bike catalog
const ProductCard = ({ 
  title, 
  price, 
  boldDescription, 
  greyDescription, 
  material, 
  weight, 
  description, 
  description2,
  productLink,
  coverImage, 
  image,
  hoverImage,
  image2,
  category,
  defaultCoverImage,
  defaultHoverImage
}) => {
  // Determine default images based on category if not explicitly provided
  const getDefaultCoverImage = () => {
    if (defaultCoverImage) return defaultCoverImage;
    
    const categoryLower = category ? category.toLowerCase() : '';
    
    if (categoryLower === 'road' || categoryLower === 'grooty' || title?.toLowerCase().includes('grooty')) 
      return "/images/road-frame-product.jpg";
    else if (categoryLower === 'gravel' || categoryLower === 'branchy' || title?.toLowerCase().includes('branchy')) 
      return "/images/gravel-frame-product.jpg";
    else if (categoryLower === 'mtb' || categoryLower === 'woody' || title?.toLowerCase().includes('woody')) 
      return "/images/mtb-frame-product-cover.jpg";
    
    return "/images/road-frame-product.jpg"; // Fallback
  };
  
  const getDefaultHoverImage = () => {
    if (defaultHoverImage) return defaultHoverImage;
    
    const categoryLower = category ? category.toLowerCase() : '';
    
    if (categoryLower === 'road' || categoryLower === 'grooty' || title?.toLowerCase().includes('grooty')) 
      return "/images/bike-forest.jpg";
    else if (categoryLower === 'gravel' || categoryLower === 'branchy' || title?.toLowerCase().includes('branchy')) 
      return "/images/bickePacking.jpeg";
    else if (categoryLower === 'mtb' || categoryLower === 'woody' || title?.toLowerCase().includes('woody')) 
      return "/images/mtb-frame-product-hover.jpg";
    
    return "/images/bike-forest.jpg"; // Fallback
  };

  // Function to safely generate image URLs from Sanity
  const safeUrlForImage = (imageObj) => {
    if (!imageObj) return null;
    
    try {
      // Use urlForImage to get the base URL
      const imageUrl = urlForImage(imageObj);
      return imageUrl;
    } catch (error) {
      console.error('Error generating image URL:', error);
      return null;
    }
  };

  // Format the price using a more robust approach
  const determinePrice = () => {
    // First check if we have a direct price value (either number or string)
    if (price !== undefined && price !== null) {
      // If it's a number, format it
      if (typeof price === 'number') {
        return `${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")} €`;
      }
      // If it's a string and not empty
      else if (typeof price === 'string' && price.trim() !== '') {
        return `${price} €`;
      }
    }
    
    // If no direct price, use title-based fallbacks based on bike type
    if (title) {
      if (title.includes("Frame")) return "2 750 €";
      
      if (category === 'Road' || title?.includes('Grooty')) {
        if (title.includes("Setup 1")) return "6 900 €";
        if (title.includes("Setup 2")) return "4 882 €";
      } 
      else if (category === 'Gravel' || title?.includes('Branchy')) {
        if (title.includes("Setup 1")) return "6 400 €";
        if (title.includes("Setup 2")) return "4 688 €";
      }
      else if (category === 'MTB' || title?.includes('Woody')) {
        if (title.includes("Setup 1")) return "6 250 €";
        if (title.includes("Setup 2")) return "4 750 €";
      }
    }
    
    // Last resort default
    return "Contact us";
  };

  // Get the price display text
  const priceText = determinePrice();

  return (
    <div className="relative overflow-hidden rounded-lg border border-wood-brown/20 transition-all duration-500 hover:shadow-xl h-full">
      <div className="flex flex-col h-full">
        {/* Image container - maintain same height */}
        <Link href={productLink || "#"} className="block w-full">
          <div className="w-full aspect-[16/9] relative group">
            {/* Primary Image */}
            <div className="absolute inset-0 transition-opacity duration-500 ease-in-out group-hover:opacity-0 z-10">
              <Image
                src={coverImage ? safeUrlForImage(coverImage) : (image ? safeUrlForImage(image) : getDefaultCoverImage())}
                alt={coverImage?.alt || (image?.alt || title || "Product")}
                fill
                loader={sanityLoader}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                className="object-cover"
              />
            </div>
            {/* Hover Image */}
            <div className="absolute inset-0 opacity-0 transition-opacity duration-500 ease-in-out group-hover:opacity-100 z-20">
              <Image
                src={hoverImage ? safeUrlForImage(hoverImage) : (image2 ? safeUrlForImage(image2) : getDefaultHoverImage())}
                alt={hoverImage?.alt || (image2?.alt || title || "Product Hover")}
                fill
                loader={sanityLoader}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                className="object-cover"
              />
            </div>
          </div>
        </Link>
        
        {/* Info section - increased height slightly */}
        <div className="bg-gray-100 border-t border-wood-brown/20 p-3 flex flex-col min-h-[80px] h-full">
          {/* Title - clickable */}
          <Link href={productLink || "#"} className="block">
            <h3 className="text-lg font-neuropol text-accent-color hover:underline">
              {title || "Product Title"}
            </h3>
          </Link>
          
          {/* Description - not clickable */}
          <div className="flex flex-col justify-between flex-grow">
            <div>
              <p className="text-sm font-bold mt-1">{boldDescription || description || ""}</p>
              <p className="text-sm text-gray-700">{greyDescription || (material ? `${material}${weight ? `, Weight: ${weight}` : ""}` : description2 || "")}</p>
            </div>
            
            {/* Price display as simple black text */}
            {price && (
              <div className="mt-4">
                <p className="font-bold text-black text-sm">
                  {priceText}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard; 